import React, { FC, memo, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import usePagination from '@mui/material/usePagination';
import { ButtonStyled, PageButton, Wrapper } from './styles';
import { ReactComponent as ArrowBackIcon } from 'applicaiton/assets/arrow-back.svg';
import { ReactComponent as ArrowForwardIcon } from 'applicaiton/assets/arrow-forward.svg';
import { PaginationProps } from './modules';

/**
 * A React component that renders a pagination control.
 *
 * @param pages - The total number of pages in the pagination.
 * @param setCurrentPage - A function to set the current page.
 * @param itemCount - The total number of items being paginated.
 * @param currentPage - The currently selected page.
 */
const Pagination: FC<PaginationProps> = ({
  pages,
  setCurrentPage,
  itemCount,
  currentPage,
}) => {
  const { t } = useTranslation();

  /**
   * A hook that returns pagination state and a function to update it.
   *
   * @param count - The total number of pages.
   * @param page - The currently selected page.
   * @param onChange - A function to handle changes in the selected page.
   */
  const { items } = usePagination({
    count: pages,
    page: currentPage,
    onChange: (e, page) => {
      setCurrentPage(page);
    },
  });

  useEffect(() => {
    const selectedPage = items.find((item) => item.selected);
    if (selectedPage) {
      setCurrentPage(selectedPage.page!);
    }
  }, [items, currentPage]);

  useEffect(() => {
    if (itemCount === 0) {
      setCurrentPage(currentPage === 1 ? 1 : currentPage! - 1);
    }
  }, [itemCount, currentPage]);

  /**
   * A memoized value that returns the previous pagination button item.
   *
   * @param items - An array of pagination items.
   */
  const prevButton = useMemo(
    () => items.find((item) => item.type === 'previous'),
    [items, currentPage],
  );

  /**
   * A memoized value that returns the next pagination button item.
   *
   * @param items - An array of pagination items.
   */
  const nextButton = useMemo(
    () => items.find((item) => item.type === 'next'),
    [items, currentPage],
  );

  /**
   * A memoized value that returns an array of pagination page items.
   *
   * @param items - An array of pagination items.
   */
  const pageList = useMemo(
    () =>
      items.filter((item) => item.type !== 'next' && item.type !== 'previous'),
    [items, currentPage],
  );

  return (
    <Wrapper>
      <ButtonStyled {...prevButton!} type="button">
        <ArrowBackIcon /> {t('back')}
      </ButtonStyled>{' '}
      <div>
        {pageList.map(({ page, type, selected, ...item }, index) => {
          let children = null;
          if (type === 'start-ellipsis' || type === 'end-ellipsis') {
            children = '…';
          } else if (type === 'page') {
            children = (
              <PageButton currentPage={selected} type="button" {...item}>
                {page}
              </PageButton>
            );
          }
          return <div key={index}>{children}</div>;
        })}
      </div>
      <ButtonStyled {...nextButton} type="button">
        {t('next')} <ArrowForwardIcon />
      </ButtonStyled>
    </Wrapper>
  );
};

export default memo(Pagination);
